import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Gate } from '../../../global/contexts/auth/auth';
import Picker from '../../../common/components/picker/picker';
import Toggle from '../../../common/components/toggle/toggle';
import { IconEdit, IconGithub } from '../../../common/icons';
import { resolveProject, searchProjects, useProjectsList } from '../../projects/contexts/projects';
import { REPOSITORIES_PAGE_SIZE, useRepositoriesList } from '../contexts/repositories';
import DeactivateButton from '../../../common/components/deactivate/deactivate-button';
import { repositoryKeys } from '../queries/keys';
import { repositoryPaths } from '../queries/paths';
import { KIND_LIST } from '../utils/kinds';
import KindBadge from './kind-badge';
import { isGithub } from '../utils/remote';

import '../styles/overview.css';

// `GET /admin/repositories` answers plain rows — its crud() declares no
// `decorate`, so the project arrives as an id and nothing else. The names come
// from a second request, and it sees the first hundred projects: past that a
// row falls back to `#id`, which is less but never wrong.
const PROJECT_LOOKUP_LIMIT = 100;

// Rows: a repository is a line of facts — kind, project, remote, branch — and
// the useful reading is down the column, not card by card.
//
// Two things the column does that a plain table does not:
//
//   · THE KIND IS A SHAPE BEFORE IT IS A WORD. Five glyphs, five tones, and the
//     word always next to them — a scanned column is read by silhouette, and
//     colour alone is not a label anybody can rely on.
//   · The remote says WHERE it lives when the panel can name the host. Today
//     that is GitHub and nothing else, because it is the only mark we have that
//     somebody would recognise.

function RepositoryRow({ repository, projectName, showProject = true }) {
    return (
        <li className="repo-row">
            <KindBadge kind={repository.kind} />

            <span className="repo-row__identity">
                <Link to={`/admin/repositories/${repository.id}`} className="repo-row__name">
                    {repository.name}
                </Link>
                {/* Inside a group the project is the heading above, so repeating
                    it on every row would be the same word forty times. */}
                {showProject && (
                    <span className="repo-row__project">
                        {projectName || `Proyecto #${repository.project_id}`}
                    </span>
                )}
            </span>

            {/* The remote is the one field somebody copies, so it shows whole
                and in monospace rather than being summarised. */}
            <span className="repo-row__remote" title={repository.remote}>
                {/* Only marked when the panel can NAME the host, which today is
                    GitHub and nothing else. */}
                {isGithub(repository.remote) && (
                    <span className="repo-row__host" title="GitHub" aria-label="GitHub">
                        <IconGithub />
                    </span>
                )}
                <span className="repo-row__remote-url">{repository.remote}</span>
            </span>

            <span className="repo-row__branch">{repository.main_branch || 'main'}</span>

            {/* Derived from the remote by the backend: it says whether the
                machine will be asked for a token or for a key. */}
            <span className="repo-row__protocol">{repository.clone_protocol || '—'}</span>

            <span className="row-actions">
                <Gate domain="repositories:write">
                    <Link
                        to={`/admin/repositories/${repository.id}`}
                        className="repo-row__edit"
                        aria-label={`Editar ${repository.name}`}
                        title="Editar"
                    >
                        <IconEdit />
                    </Link>
                </Gate>
                <Gate domain="repositories:admin">
                    <DeactivateButton
                        name={repository.name}
                        noun="repositorio"
                        path={repositoryPaths.detail(repository.id)}
                        invalidate={[repositoryKeys.all]}
                    />
                </Gate>
            </span>
        </li>
    );
}

function ListHead({ withProject }) {
    return (
        <div className="repo-row repo-row--head" aria-hidden="true">
            <span>Tipo</span>
            <span>{withProject ? 'Repositorio' : 'Nombre'}</span>
            <span>Remoto</span>
            <span>Rama</span>
            <span>Clona</span>
            <span />
        </div>
    );
}

export default function RepositoriesOverview({ embedded = false }) {
    const [params, setParams] = useSearchParams();

    const page = Math.max(Number(params.get('page')) || 1, 1);
    const projectId = params.get('project') || '';
    const kind = params.get('kind') || '';
    const grouped = params.get('group') === 'project';
    const offset = (page - 1) * REPOSITORIES_PAGE_SIZE;

    const { data, isLoading, isFetching, error } = useRepositoriesList({
        limit: REPOSITORIES_PAGE_SIZE,
        offset,
        projectId,
        kind,
    });

    const { data: projectPage } = useProjectsList({ limit: PROJECT_LOOKUP_LIMIT, offset: 0 });

    const projectNames = useMemo(() => {
        const map = new Map();
        for (const project of projectPage?.items ?? []) map.set(project.id, project.name);
        return map;
    }, [projectPage]);

    const items = useMemo(() => data?.items ?? [], [data]);
    const total = data?.pagination?.total ?? 0;
    const lastPage = useMemo(
        () => Math.max(Math.ceil(total / REPOSITORIES_PAGE_SIZE), 1),
        [total],
    );

    // LOS GRUPOS SE ARMAN SOBRE LA PÁGINA, y no hay otra forma: el listado
    // pagina del lado del servidor y no devuelve nada agrupado. So a project
    // whose repositories straddle two pages appears on both, with part of its
    // list each time. The screen says so rather than letting a short group read
    // as a complete one.
    const groups = useMemo(() => {
        if (!grouped) return null;

        const byProject = new Map();
        for (const repository of items) {
            const key = repository.project_id ?? 0;
            if (!byProject.has(key)) {
                byProject.set(key, {
                    id: key,
                    // `#id` is the fallback, never a blank heading: a project
                    // past the lookup's first hundred still gets a group.
                    name: projectNames.get(key) ?? null,
                    rows: [],
                });
            }
            byProject.get(key).rows.push(repository);
        }

        return [...byProject.values()].sort((a, b) => (
            (a.name ?? `#${a.id}`).localeCompare(b.name ?? `#${b.id}`, 'es')
        ));
    }, [grouped, items, projectNames]);

    const setFilter = (key, value) => {
        const target = new URLSearchParams(params);
        if (value) target.set(key, value);
        else target.delete(key);
        target.delete('page');
        setParams(target);
    };

    const goToPage = (next) => {
        const target = new URLSearchParams(params);
        if (next <= 1) target.delete('page');
        else target.set('page', String(next));
        setParams(target);
    };

    const hasFilters = !!(projectId || kind || grouped);

    if (isLoading) return <p className="repos__loading">Cargando repositorios...</p>;

    if (error) {
        return (
            <p className="repos__error">
                {error.message || 'No se pudieron cargar los repositorios'}
            </p>
        );
    }

    return (
        <section className="repos">
            {/* Embebido en la sección de Proyectos, el título lo pone ella.
                El botón de alta se queda: es de repositorios, no de la
                sección, y el de proyecto vive arriba. */}
            <header className="repos__header">
                <div className="repos__heading">
                    <span className="repos__count">
                        {total} {total === 1 ? 'repositorio' : 'repositorios'}
                    </span>
                    {!embedded && <h2 className="repos__title">Repositorios</h2>}
                </div>

                <Gate domain="repositories:write">
                    <Link
                        to={`/admin/repositories/new${projectId ? `?project=${projectId}` : ''}`}
                        className="repos__new"
                    >
                        + Nuevo repositorio
                    </Link>
                </Gate>
            </header>

            <div className="repos__filters">
                {/* UN BUSCADOR Y NO UN SELECTOR.
                    The `<select>` here listed the first hundred projects and
                    hoped: past that, the project could not be chosen at all,
                    and a filter already set to one of them rendered as `#57`.
                    `GET /admin/projects` takes `?q=`, so the list can come from
                    the search instead of from luck — and `resolveProject` gets
                    the name of whatever is already selected. */}
                <label className="repos__filter-field">
                    <span className="repos__filter-label">Proyecto</span>
                    <Picker
                        value={projectId}
                        onChange={(id) => setFilter('project', id)}
                        fetchOptions={searchProjects}
                        resolveOption={resolveProject}
                        cacheKey="projects"
                        labelOf={project => project.name}
                        subtitleOf={project => project.slug}
                        placeholder="Todos los proyectos"
                        emptyLabel="Todos los proyectos"
                    />
                </label>

                <label className="repos__filter-field">
                    <span className="repos__filter-label">Tipo</span>
                    <select
                        className="repos__filter"
                        value={kind}
                        onChange={e => setFilter('kind', e.target.value)}
                    >
                        <option value="">Todos los tipos</option>
                        {KIND_LIST.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                </label>

                {/* A switch and not a checkbox: it turns a MODE on — it changes
                    how the list is drawn, not which rows it shows — and that
                    reads as on/off rather than as one ticked option among the
                    filters beside it. */}
                <div className="repos__group">
                    <Toggle
                        checked={grouped}
                        onChange={(on) => setFilter('group', on ? 'project' : '')}
                        label="Agrupar por proyecto"
                        hint={grouped ? 'Un bloque por proyecto' : 'Una sola lista'}
                    />
                </div>

                {hasFilters && (
                    <button
                        type="button"
                        className="repos__filter-clear"
                        onClick={() => setParams(new URLSearchParams())}
                    >
                        Limpiar
                    </button>
                )}
            </div>

            {/* The grouping is over the page, so a project split across two
                pages shows up on both with part of its list each time. */}
            {grouped && total > items.length && (
                <p className="repos__scope-note">
                    Los grupos se arman sobre <strong>esta página</strong> de {items.length}, no
                    sobre los {total} repositorios: el listado pagina en el servidor y no
                    devuelve nada agrupado. Filtrá por un proyecto para verlo completo.
                </p>
            )}

            {!items.length && (
                <p className="repos__empty">
                    {hasFilters
                        ? 'Ningún repositorio coincide con estos filtros.'
                        : 'Todavía no hay repositorios cargados.'}
                </p>
            )}

            {!!items.length && !grouped && (
                <div className={`repos__list ${isFetching ? 'repos__list--refreshing' : ''}`}>
                    <ListHead withProject />
                    <ul className="repos__rows">
                        {items.map(repository => (
                            <RepositoryRow
                                key={repository.id}
                                repository={repository}
                                projectName={projectNames.get(repository.project_id)}
                            />
                        ))}
                    </ul>
                </div>
            )}

            {!!items.length && grouped && (
                <div className={`repos__groups ${isFetching ? 'repos__list--refreshing' : ''}`}>
                    {groups.map(group => (
                        <section key={group.id} className="repos__group-block">
                            <h3 className="repos__group-title">
                                <Link to={`/admin/projects/${group.id}`}>
                                    {group.name ?? `Proyecto #${group.id}`}
                                </Link>
                                <span className="repos__group-count">
                                    {group.rows.length}
                                    {group.rows.length === 1 ? ' repositorio' : ' repositorios'}
                                </span>
                            </h3>

                            <div className="repos__list">
                                <ListHead withProject={false} />
                                <ul className="repos__rows">
                                    {group.rows.map(repository => (
                                        <RepositoryRow
                                            key={repository.id}
                                            repository={repository}
                                            showProject={false}
                                        />
                                    ))}
                                </ul>
                            </div>
                        </section>
                    ))}
                </div>
            )}

            {total > REPOSITORIES_PAGE_SIZE && (
                <nav className="repos__pager">
                    <button
                        type="button"
                        className="repos__pager-button"
                        onClick={() => goToPage(page - 1)}
                        disabled={page <= 1}
                    >
                        Anterior
                    </button>
                    <span className="repos__pager-position">Página {page} de {lastPage}</span>
                    <button
                        type="button"
                        className="repos__pager-button"
                        onClick={() => goToPage(page + 1)}
                        disabled={page >= lastPage}
                    >
                        Siguiente
                    </button>
                </nav>
            )}
        </section>
    );
}
